import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import WaitlistApiService from '../../api/WaitlistApiService';
import ConfirmDeleteModal from '../Modal/StandardFunctions/ConfirmDeleteModal';
import JoinWaitlistModal from '../Modal/StandardFunctions/JoinWaitlistModal';

function formatDateTime(value) {
    if (!value) return '';
    return new Date(value).toLocaleString();
}

function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString();
}

export default function MyWaitlist(props) {
    const { setSnackbarProperties, isMobile } = props;

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState(0);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [joinModalOpen, setJoinModalOpen] = useState(false);

    const waitlistApiService = new WaitlistApiService();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'Executive Storage Suites - My Waitlist';
        async function load() {
            setLoading(true);
            const response = await waitlistApiService.getMyEntries();
            if (response?.success) {
                setEntries(response.data || []);
            } else {
                setSnackbarProperties('Unable to load your waitlist', 'error');
            }
            setLoading(false);
        }
        load();
    }, [reload]);

    function bumpReload() {
        setReload((r) => (r >= 100 ? 0 : r + 1));
    }

    function handleLeaveClick(entry) {
        setSelectedEntry(entry);
        setConfirmLeave(true);
    }

    async function handleConfirmLeave() {
        if (!selectedEntry) return;
        const response = await waitlistApiService.leave(selectedEntry.id);
        setConfirmLeave(false);
        setSelectedEntry(null);

        if (response?.success) {
            setSnackbarProperties('Removed from waitlist', 'warning');
            bumpReload();
        } else {
            setSnackbarProperties(response?.message || 'Unable to leave waitlist', 'error');
        }
    }

    return (
        <Box sx={{ p: isMobile ? 1 : 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">My Waitlist</Typography>
                <Button variant="contained" color="primary" onClick={() => setJoinModalOpen(true)}>
                    Join Waitlist
                </Button>
            </Stack>

            {loading && (
                <Backdrop open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            {!loading && entries.length === 0 && (
                <Typography variant="body1">You&apos;re not on any waitlists right now.</Typography>
            )}

            <Stack spacing={2}>
                {entries.map((entry) => (
                    <Card key={entry.id} variant="outlined">
                        <CardContent>
                            <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} spacing={1}>
                                <Box>
                                    <Typography variant="h6">{entry.facilityName} — {entry.storageSize}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Joined {formatDate(entry.joinedUtc)}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {entry.pendingOffer ? (
                                        <Chip label="Offer pending" color="success" />
                                    ) : (
                                        <Chip label="On the list" color="primary" variant="outlined" />
                                    )}
                                    <Button color="warning" onClick={() => handleLeaveClick(entry)}>Leave</Button>
                                </Stack>
                            </Stack>

                            {entry.pendingOffer && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#eafaf1', borderLeft: '4px solid #27ae60' }}>
                                    <Typography variant="body1">
                                        A unit is available for you: <strong>{entry.pendingOffer.storageUnitName}</strong>
                                        {entry.pendingOffer.availableFrom && (
                                            <> (available from {formatDate(entry.pendingOffer.availableFrom)})</>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Respond by {formatDateTime(entry.pendingOffer.expiresUtc)}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        sx={{ mt: 1 }}
                                        onClick={() => navigate(`/waitlist/offer/${entry.pendingOffer.responseToken}`)}
                                    >
                                        Review Offer
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            <ConfirmDeleteModal
                open={confirmLeave}
                title="Leave Waitlist"
                confirmText="Are you sure you want to leave this waitlist? If you have a pending offer, it will be declined."
                handleConfirm={handleConfirmLeave}
                close={() => setConfirmLeave(false)}
            />

            <JoinWaitlistModal
                open={joinModalOpen}
                close={() => setJoinModalOpen(false)}
                setSnackbarProperties={setSnackbarProperties}
                onJoined={bumpReload}
            />
        </Box>
    );
}

MyWaitlist.propTypes = {
    setSnackbarProperties: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
};